// AODN adapter — Australia's Ocean Data Network national real-time wave feed. One
// GeoServer WFS covers every state wave-buoy network (IMOS, BOM, Qld, WA, NSW, VIC).
// Reached through the same-origin /aodn relay (GeoServer sends no CORS header).
//
// Catalog: the "_map" layer is a rolling recent-observations feed (one row per
// buoy per timestamp), so we filter TIME to the last few hours and keep the latest
// row per site — that yields ~one point per currently-reporting buoy, with the
// latest reading and coordinates inline.
// Obs: the "_data" layer holds the timeseries; we pull it by file_id and map the
// wave columns onto BuoyObs. AODN wave buoys report NO wind/visibility/pressure —
// those fields stay null (the honest asymmetry: Australia gives waves, not wind).
//
// Field map: WSSH → significant wave height (m); WPPE → peak/dominant wave period
// (s, the DPD analogue). Data is real-time / non-QC, hourly, positions ~1–2 km.

import type { BuoyObs, BuoyRef, BuoySource, BuoyStation } from "./types.js";

const AODN_OWS = "/aodn/geoserver/ows";
const MAP_LAYER = "aodn_wave_nrt_v2_timeseries_map";
const DATA_LAYER = "aodn_wave_nrt_v2_timeseries_data";
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
// How far back to look for "currently reporting" buoys when building the catalog.
const CATALOG_WINDOW_HOURS = 3;

function record(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null;
}
function num(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}
function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

async function getJson(url: string): Promise<unknown> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

function featureArray(json: unknown): Record<string, unknown>[] {
  const fs = record(json)?.["features"];
  if (!Array.isArray(fs)) return [];
  const out: Record<string, unknown>[] = [];
  for (const f of fs) {
    const rec = record(f);
    if (rec) out.push(rec);
  }
  return out;
}

function wfsUrl(layer: string, extra: string): string {
  return (
    `${AODN_OWS}?service=WFS&version=1.1.0&request=GetFeature` +
    `&typeName=aodn:${layer}&outputFormat=application/json&sortBy=TIME+D&${extra}`
  );
}

async function loadAodnCatalog(): Promise<BuoyStation[]> {
  const after = new Date(Date.now() - CATALOG_WINDOW_HOURS * MS_PER_HOUR)
    .toISOString()
    .replace(/\.\d+Z$/, "Z");
  const url = wfsUrl(
    MAP_LAYER,
    `CQL_FILTER=${encodeURIComponent(`TIME AFTER ${after}`)}`,
  );
  const features = featureArray(await getJson(url));
  // Rows arrive newest-first; the first row seen for a site is its latest.
  const bySite = new Map<string, BuoyStation>();
  for (const f of features) {
    const props = record(f["properties"]);
    const geom = record(f["geometry"]);
    const coords = geom?.["coordinates"];
    if (!props || !Array.isArray(coords)) continue;
    const lon = num(coords[0]);
    const lat = num(coords[1]);
    const site = str(props["site_name"]);
    const id = props["file_id"] == null ? "" : String(props["file_id"]);
    if (lat === null || lon === null || site === "" || id === "") continue;
    if (bySite.has(site)) continue;
    bySite.set(site, { source: "aodn", id, name: site, lat, lon });
  }
  return [...bySite.values()];
}

async function fetchAodnObs(id: string): Promise<BuoyObs | null> {
  const url = wfsUrl(
    DATA_LAYER,
    `maxFeatures=400&CQL_FILTER=${encodeURIComponent(`file_id=${id}`)}`,
  );
  const features = featureArray(await getJson(url));
  if (features.length === 0) return null;

  interface Row {
    t: number;
    wvhtM: number | null;
    dpdS: number | null;
  }
  const rows: Row[] = [];
  for (const f of features) {
    const p = record(f["properties"]);
    if (!p) continue;
    const t = Date.parse(str(p["TIME"]));
    if (!Number.isFinite(t)) continue;
    rows.push({ t, wvhtM: num(p["WSSH"]), dpdS: num(p["WPPE"]) });
  }
  if (rows.length === 0) return null;

  // Ascending by time, keep the last ~4 days — matching the NDBC parser's window.
  rows.sort((a, b) => a.t - b.t);
  const cutoff = rows[rows.length - 1].t - 4 * MS_PER_DAY;
  const kept = rows.filter((r) => r.t >= cutoff);
  const nulls = (): (number | null)[] => kept.map(() => null);
  return {
    timesUtcMs: kept.map((r) => r.t),
    wvhtM: kept.map((r) => r.wvhtM),
    dpdS: kept.map((r) => r.dpdS),
    wspdKn: nulls(),
    gstKn: nulls(),
    visNmi: nulls(),
    wdirDeg: nulls(),
    presHpa: nulls(),
    atmpC: nulls(),
    wtmpC: nulls(),
  };
}

export const aodnSource: BuoySource = {
  key: "aodn",
  label: "AODN",
  attribution: "IMOS / AODN wave buoys (CC-BY 4.0)",
  loadCatalog: loadAodnCatalog,
  fetchObs: fetchAodnObs,
  // AODN has no simple per-station web page; the console is the destination.
  stationUrl: () => null,
  pickLabel: (ref: BuoyRef) => `AODN · ${ref.name}`,
};
